<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import vivoCopilotAPI from 'dashboard/api/vivoCopilot';
import Button from 'dashboard/components-next/button/Button.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxType: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();
const { updateUISettings } = useUISettings();
const turns = ref([]);
const prompt = ref('');
const loading = ref(false);
const submitting = ref(false);
const error = ref('');
let poller = null;

const messages = computed(() =>
  turns.value.flatMap(turn => {
    const items = [
      { id: `${turn.id}-user`, role: 'user', content: turn.prompt },
    ];
    if (turn.response?.content) {
      items.push({
        id: `${turn.id}-assistant`,
        role: 'assistant',
        content: turn.response.content,
        replySuggestion: turn.response.reply_suggestion === true,
      });
    }
    return items;
  })
);

const active = computed(() =>
  turns.value.some(turn => turn.status === 'processing')
);

const stopPolling = () => {
  if (poller) window.clearInterval(poller);
  poller = null;
};

const applyState = (data, requestedConversationId) => {
  if (requestedConversationId !== props.conversationId) return;

  turns.value = data.turns;
  if (!data.turns.some(turn => turn.status === 'processing')) stopPolling();
};

const poll = async requestedConversationId => {
  try {
    const { data } = await vivoCopilotAPI.show(requestedConversationId);
    applyState(data, requestedConversationId);
  } catch {
    // Keep the draft-only panel usable and retry on the next bounded poll.
  }
};

const startPolling = requestedConversationId => {
  stopPolling();
  poller = window.setInterval(() => poll(requestedConversationId), 1000);
};

const load = async () => {
  const requestedConversationId = props.conversationId;
  stopPolling();
  loading.value = true;
  error.value = '';
  try {
    const { data } = await vivoCopilotAPI.show(requestedConversationId);
    applyState(data, requestedConversationId);
    if (
      requestedConversationId === props.conversationId &&
      data.turns.some(turn => turn.status === 'processing')
    ) {
      startPolling(requestedConversationId);
    }
  } catch {
    if (requestedConversationId === props.conversationId) {
      turns.value = [];
      error.value = t('VIVO_COPILOT.LOAD_ERROR');
    }
  } finally {
    if (requestedConversationId === props.conversationId) loading.value = false;
  }
};

const submit = async () => {
  const content = prompt.value.trim();
  if (!content || submitting.value) return;

  const requestedConversationId = props.conversationId;
  submitting.value = true;
  error.value = '';
  try {
    const { data } = await vivoCopilotAPI.create(requestedConversationId, {
      prompt: content,
      request_id: crypto.randomUUID(),
    });
    if (requestedConversationId === props.conversationId) {
      applyState(data, requestedConversationId);
      prompt.value = '';
      if (data.turns.some(turn => turn.status === 'processing')) {
        startPolling(requestedConversationId);
      }
    }
  } catch (requestError) {
    if (requestedConversationId === props.conversationId) {
      error.value =
        requestError.response?.status === 409
          ? t('VIVO_COPILOT.ACTIVE_TURN')
          : t('VIVO_COPILOT.SAFE_ERROR');
    }
  } finally {
    submitting.value = false;
  }
};

const useDraft = content => {
  const event = [INBOX_TYPES.WEB, INBOX_TYPES.EMAIL].includes(props.inboxType)
    ? BUS_EVENTS.INSERT_INTO_RICH_EDITOR
    : BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR;
  emitter.emit(event, content);
};

const close = () => {
  updateUISettings({
    is_copilot_panel_open: false,
    is_contact_sidebar_open: false,
  });
};

watch(() => props.conversationId, load);
onMounted(load);
onBeforeUnmount(stopPolling);
</script>

<template>
  <div class="flex flex-col h-full w-full text-sm text-n-slate-12">
    <SidebarActionsHeader :title="$t('VIVO_COPILOT.TITLE')" @close="close" />

    <div class="border-b border-n-weak bg-n-alpha-2 px-4 py-3">
      <div class="font-medium">{{ $t('VIVO_COPILOT.DRAFT_ONLY') }}</div>
      <div class="text-xs text-n-slate-10 mt-1">
        {{ $t('VIVO_COPILOT.DRAFT_ONLY_HELP') }}
      </div>
      <div v-if="active" class="text-xs text-n-iris-11 mt-2">
        {{ $t('VIVO_COPILOT.WORKING') }}
      </div>
    </div>

    <div class="flex-1 overflow-y-auto px-4 py-4 space-y-4">
      <div v-if="loading" class="text-n-slate-10">
        {{ $t('VIVO_COPILOT.LOADING') }}
      </div>
      <div v-else-if="!messages.length && !error" class="text-n-slate-10">
        {{ $t('VIVO_COPILOT.EMPTY') }}
      </div>

      <div
        v-for="message in messages"
        :key="message.id"
        class="rounded-lg border border-n-weak p-3"
        :class="message.role === 'user' ? 'bg-n-alpha-2' : 'bg-n-solid-2'"
      >
        <div class="font-medium mb-1">
          {{
            message.role === 'user'
              ? $t('VIVO_COPILOT.OPERATOR')
              : $t('VIVO_COPILOT.VIVI')
          }}
        </div>
        <div class="whitespace-pre-wrap break-words">{{ message.content }}</div>
        <div v-if="message.replySuggestion" class="mt-3">
          <div class="text-xs text-n-slate-10 mb-2">
            {{ $t('VIVO_COPILOT.DRAFT_LABEL') }}
          </div>
          <Button
            data-testid="vivo-copilot-use-draft"
            :label="$t('VIVO_COPILOT.USE')"
            faded
            sm
            slate
            @click="useDraft(message.content)"
          />
        </div>
      </div>

      <div v-if="error" class="rounded-lg bg-n-ruby-3 text-n-ruby-11 p-3">
        {{ error }}
      </div>
    </div>

    <form class="border-t border-n-weak p-3" @submit.prevent="submit">
      <textarea
        v-model="prompt"
        :placeholder="$t('VIVO_COPILOT.PLACEHOLDER')"
        class="w-full reset-base bg-n-alpha-3 px-3 py-2 text-sm border border-n-weak rounded-lg resize-none text-n-slate-12"
        rows="3"
        :disabled="submitting"
      />
      <Button
        class="mt-2 w-full"
        :label="$t('VIVO_COPILOT.ASK')"
        type="submit"
        :is-loading="submitting"
        :disabled="!prompt.trim() || submitting"
      />
    </form>
  </div>
</template>
